import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Switch } from 'src/@/components/ui/switch';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import widgetCmsService from 'src/services/widgetCmsService';
import RichEditor from './RichEditor';
import ImageUploadField from './ImageUploadField';
import { WIDGET_CMS_DESIGN as D } from './WidgetCms.design';

export default function NewsPostDrawer({ projetoId, post, onClose, onSaved }) {
    const { t } = useTranslation('settings');
    const isEdit = !!post?._id;
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [tag, setTag] = useState('NOVIDADE');
    const [imagem, setImagem] = useState('');
    const [banner, setBanner] = useState('');
    const [corpo, setCorpo] = useState('');
    const [publicado, setPublicado] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (post) {
            setTitulo(post.titulo || '');
            setDescricao(post.descricao || '');
            setTag(post.tag || 'NOVIDADE');
            setImagem(post.imagem || '');
            setBanner(post.banner || '');
            setCorpo(post.corpo || '');
            setPublicado(!!post.publicado);
        }
    }, [post]);

    const handleSave = async () => {
        if (!titulo.trim()) {
            setError(t('widgetCms.newsPost.titleRequiredError', 'O título é obrigatório'));
            return;
        }
        setSaving(true);
        setError(null);
        const data = { titulo, descricao, tag, imagem, banner, corpo, publicado };
        try {
            if (isEdit) {
                await widgetCmsService.editarNews(post._id, data);
            } else {
                await widgetCmsService.criarNews(projetoId, data);
            }
            onSaved();
        } catch (e) {
            setError(e?.message || t('widgetCms.newsPost.saveError', 'Erro ao salvar o post'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={D.drawer.overlay}>
            <div className={D.drawer.backdrop} onClick={onClose} />
            <div className={D.drawer.containerNarrow}>
                <div className={D.drawer.header.container}>
                    <div className={D.drawer.header.left}>
                        <div className={`${D.drawer.header.iconBox} ${isEdit ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-[#7C3AED]'}`}>
                            <Iconify icon="solar:document-add-bold" width={20} />
                        </div>
                        <div>
                            <h2 className={D.drawer.header.title}>{isEdit ? t('widgetCms.newsPost.editTitle', 'Editar post') : t('widgetCms.newsPost.createTitle', 'Novo post de novidade')}</h2>
                            <p className={D.drawer.header.subtitle}>{t('widgetCms.newsPost.headerSubtitle', 'Publicado na aba Novidades do widget')}</p>
                        </div>
                    </div>
                    <button className={D.drawer.header.closeButton} onClick={onClose}>
                        <Iconify icon="eva:close-outline" width={20} />
                    </button>
                </div>

                {error && (
                    <div className={D.drawer.errorBanner}>
                        <Iconify icon="eva:alert-circle-outline" width={16} />
                        {error}
                    </div>
                )}

                <div className={D.drawer.content.container}>
                    <div className={D.drawer.content.leftColumn}>
                        <div className="flex flex-col gap-5">
                            <div>
                                <label className={D.drawer.section.label}>{t('labels.title', 'Título', { ns: 'common' })}</label>
                                <Input
                                    value={titulo}
                                    onChange={e => setTitulo(e.target.value)}
                                    placeholder={t('widgetCms.newsPost.titlePlaceholder', 'Ex.: Novo painel de métricas 📊')}
                                    className={D.drawer.section.input}
                                />
                            </div>
                            <div>
                                <label className={D.drawer.section.label}>{t('widgetCms.shared.shortDescriptionLabel', 'Descrição curta')}</label>
                                <Textarea
                                    value={descricao}
                                    onChange={e => setDescricao(e.target.value)}
                                    placeholder={t('widgetCms.newsPost.descriptionPlaceholder', 'Resumo exibido no card do feed')}
                                    rows={2}
                                    className={D.drawer.section.input}
                                />
                            </div>
                            <div>
                                <label className={D.drawer.section.label}>{t('widgetCms.newsPost.tagLabel', 'Tag')}</label>
                                <Input
                                    value={tag}
                                    onChange={e => setTag(e.target.value.toUpperCase())}
                                    placeholder="NOVIDADE"
                                    className={D.drawer.section.input}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <ImageUploadField
                                    label={t('widgetCms.newsPost.cardImageLabel', 'Imagem do card')}
                                    value={imagem}
                                    onChange={setImagem}
                                    hint={t('widgetCms.newsPost.cardImageHint', 'Preview no feed de novidades')}
                                />
                                <ImageUploadField
                                    label={t('widgetCms.newsPost.bannerImageLabel', 'Banner do detalhe')}
                                    value={banner}
                                    onChange={setBanner}
                                    hint={t('widgetCms.newsPost.bannerImageHint', 'Topo do post aberto')}
                                />
                            </div>
                            <div>
                                <label className={D.drawer.section.label}>{t('widgetCms.shared.contentLabel', 'Conteúdo')}</label>
                                <RichEditor value={corpo} onChange={setCorpo} placeholder={t('widgetCms.newsPost.contentPlaceholder', 'Conte a novidade em detalhes...')} />
                            </div>
                        </div>
                    </div>

                    <div className={D.drawer.content.rightColumn}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('widgetCms.newsPost.previewLabel', 'Preview do card')}</p>
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-[300px] mx-auto">
                            {imagem ? (
                                <img src={imagem} alt="" className="w-full h-36 object-cover" />
                            ) : (
                                <div className="w-full h-36 bg-gradient-to-br from-violet-100 via-pink-50 to-orange-50 flex items-center justify-center">
                                    <Iconify icon="eva:image-outline" width={28} className="text-violet-300" />
                                </div>
                            )}
                            <div className="p-4">
                                <span className="inline-block text-[10px] font-bold tracking-wider text-[#7C3AED] bg-violet-50 px-2 py-0.5 rounded-full mb-2">
                                    {tag || 'NOVIDADE'}
                                </span>
                                <p className="text-sm font-bold text-gray-900 line-clamp-2">{titulo || t('widgetCms.newsPost.previewTitleFallback', 'Título da novidade')}</p>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{descricao || t('widgetCms.newsPost.previewDescriptionFallback', 'Descrição curta do post...')}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-between bg-white rounded-xl border border-gray-100 px-4 py-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">{t('widgetCms.newsPost.publishToggleLabel', 'Publicar')}</p>
                                <p className="text-xs text-gray-500">{t('widgetCms.newsPost.publishToggleHint', 'Visível no widget imediatamente')}</p>
                            </div>
                            <Switch checked={publicado} onCheckedChange={setPublicado} />
                        </div>
                        {publicado ? (
                            <Badge variant="outline" className={`${D.badge.publicado} mt-3`}>
                                <span className={D.badge.dotVerde} />
                                {t('widgetCms.newsPost.badgeWillPublish', 'Será publicado ao salvar')}
                            </Badge>
                        ) : (
                            <Badge variant="outline" className={`${D.badge.rascunho} mt-3`}>
                                <span className={D.badge.dotAmarelo} />
                                {t('status.draft', 'Rascunho', { ns: 'common' })}
                            </Badge>
                        )}
                    </div>
                </div>

                <div className={D.drawer.footer.container}>
                    <div className={D.drawer.footer.left} />
                    <div className={D.drawer.footer.right}>
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                            onClick={onClose}
                        >
                            {t('buttons.cancel', 'Cancelar', { ns: 'common' })}
                        </button>
                        <ButtonLoading loading={saving} onClick={handleSave} className={D.page.actionButton}>
                            <Iconify icon="eva:save-outline" width={16} />
                            {isEdit ? t('buttons.saveChanges', 'Salvar alterações', { ns: 'common' }) : t('widgetCms.newsPost.createButton', 'Criar post')}
                        </ButtonLoading>
                    </div>
                </div>
            </div>
        </div>
    );
}

NewsPostDrawer.propTypes = {
    projetoId: PropTypes.string.isRequired,
    post: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};
